/**
 * Copyright IBM Corp. 2022, 2026
 */

import { forwardRef } from 'react'
import type { ButtonPrimitiveProps } from '.'
import { FlightIcon, type FlightIconSize } from '../flight-icon'
import { Interactive } from '../interactive'
import classNames from 'classnames'
import s from './styles.module.scss'

// These are the only “real” colors our legacy button knows about
const LEGACY_COLORS = [
	'primary',
	'secondary',
	'tertiary',
	'critical',
	'custom',
] as const
export type LegacyButtonColor = (typeof LEGACY_COLORS)[number]

const ICON_SIZE_MAP = {
	small: 12,
	medium: 16,
	large: 24,
} as const satisfies Record<'small' | 'medium' | 'large', FlightIconSize>

export const ButtonPrimitive = forwardRef<
	HTMLButtonElement,
	ButtonPrimitiveProps
>(
	(
		{
			size = 'medium',
			color = 'primary',
			isFullWidth,
			isIconOnly,
			icon,
			iconPosition = 'leading',
			text,
			href,
			isHrefExternal,
			className,
			locale,
			...rest
		},
		ref,
	) => {
		const hasIcon = Boolean(icon)
		const showIconOnly = isIconOnly && hasIcon

		const iconElement = icon && (
			<FlightIcon name={icon} size={ICON_SIZE_MAP[size]} />
		)

		return (
			<Interactive
				className={classNames(
					s.button,
					s[`size-${size}`],
					{
						[s['width-full']]: isFullWidth,
						[s['icon-only']]: showIconOnly,
					},
					color !== 'custom' && s[`color-${color}`],
					className,
				)}
				href={rest.disabled ? '' : href}
				isHrefExternal={isHrefExternal}
				aria-label={showIconOnly ? text : undefined}
				locale={locale}
				{...rest}
				ref={ref}
			>
				{showIconOnly && icon ? (
					iconElement
				) : (
					<>
						{icon && iconPosition === 'leading' && iconElement}
						{text ? <div className={s.text}>{text}</div> : null}
						{icon && iconPosition === 'trailing' && iconElement}
					</>
				)}
			</Interactive>
		)
	},
)
ButtonPrimitive.displayName = 'ButtonPrimitive'
